import { Controller } from "@hotwired/stimulus"
import { favoriteUnfavoriteSong } from "../services/user.js"
import { fetchDataForMessagePopUp } from "../services/message.js"
import PlayHistoryContainer from "../../assets/javascripts/source/pages/history/PlayHistoryContainer.js"
import {createRoot} from "react-dom/client"
import ProgressBar from "progressbar.js"
import { openLogin } from "../services/modal.js"
import { handleCustomLicenseRequest } from "../../assets/javascripts/source/shared/utils/modalHelper.js"

// Connects to data-controller="audio-player"
export default class extends Controller {
  static values = {
    currentIndex: Number,
    currentPlaylistIndex: Number,
    playlist: Array,
    currentSongVersionId: Number,
    user: Object,
  }

  static targets = [
    "player",
    "playerAside",
    "wrapper",
    "info",
    "title",
    "length",
    "elapsed",
    "artist",
    "waveform",
    "playhead",
    "play",
    "pause",
    "playPauseButton",
    "thumbnail",
    "actions",
    "favorite",
    "download",
    "downloadMenu",
    "license",
    "projectSearch",
    "expandMenuButton",
    "expandedMenu",
    "hoverConnect",
    "backButton",
    "forwardButton",
    "history",
    "volumePanel",
    "volumeButton"
  ]

  clearPlaylist() {
    // If the song index changes and we're not on a project search, clear the playlist so it doesn't interfere with history.
    if (window.location.href.indexOf("project_search") == -1) {
      this.playlistValue = []
      this.currentPlaylistIndexValue = 0
    }
  }

  connect() {
    if (this.hasPlayerTarget) {
      // Store current state before resetting if this is a reconnection
      const isPlayerInitialized = this.element.hasAttribute('data-audio-player-initialized')
      let preservedState = {}

      this.isPlaying = false
      this.player = this.playerTarget
      this.wrapper = this.wrapperTarget
      this.isLoading = false
      this.currentRequest = null

      this.currentSongVersionIdValue = this.currentSongVersionIdValue || null
      this.csrf = document.querySelector('meta[name="csrf-token"]')?.content
      this.favoriteSongVersions =
        Array.from(JSON.parse(this.wrapper.getAttribute("data-user-favorites"))) || []

      // user play history attributes
      this.playHistory = []
      this.fetchPlayHistory()

      // Ensure event listeners are removed before adding new ones (avoid duplicates)
      if (this.boundHandleNewSongClick) {
        document.removeEventListener("play-song", this.boundHandleNewSongClick)
      }
      if (this.boundLoadSong) {
        document.removeEventListener("start-playlist", this.boundLoadSong)
      }
      if (this.boundLoadPlaylist) {
        document.removeEventListener("load-playlist", this.boundLoadPlaylist)
      }
      if (this.boundHandleFavoriteToggled) {
        window.removeEventListener("favorite-toggled", this.boundHandleFavoriteToggled)
      }

      // Bind document event listeners directly since this controller persists with data-turbo-permanent
      this.boundHandleNewSongClick = this.handleNewSongClick.bind(this)
      this.boundLoadSong = this.loadSong.bind(this)
      this.boundLoadPlaylist = this.loadPlaylist.bind(this)
      this.boundHandleFavoriteToggled = this.handleFavoriteToggled.bind(this)

      document.addEventListener("play-song", this.boundHandleNewSongClick)
      document.addEventListener("start-playlist", this.boundLoadSong)
      document.addEventListener("load-playlist", this.boundLoadPlaylist)
      window.addEventListener("favorite-toggled", this.boundHandleFavoriteToggled)

      this.playingHandler = this.handlePlaying.bind(this)
      this.pauseHandler = this.handlePause.bind(this)
      this.endedHandler = this.handleEnded.bind(this)
      this.seekHandler = this.handleSeek.bind(this)

      this.downloadMenuOpenHandler = this.openDownloadMenu.bind(this)
      this.actionsMenuOpenHandler = this.openActionsMenu.bind(this)
      this.licenseHandler = this.handleLicense.bind(this)
      this.displayLyricsHandler = this.handleDisplayLyrics.bind(this)
      this.addToMixtapeHandler = this.handleAddToMixtape.bind(this)
      this.openHistoryHandler = this.handleOpenHistory.bind(this)
      this.addToProjectSearchHandler = this.handleProjectSearch.bind(this)

      this.player.addEventListener("playing", this.playingHandler)
      this.player.addEventListener("pause", this.pauseHandler)
      this.player.addEventListener("ended", this.endedHandler)
      this.playheadTarget.addEventListener("click", this.seekHandler)
      this.downloadTarget.addEventListener("click", this.downloadMenuOpenHandler)
      this.expandMenuButtonTarget.addEventListener("click", this.actionsMenuOpenHandler)
      this.setupCanvases()

      if (isPlayerInitialized) {
        preservedState = this.preserveState()
        this.isPlaying = preservedState.isPlaying
        this.updatePlayPauseButton()
        if (this.isPlaying) {
          this.drawPlayHead()
        }
        if (this.currentSongVersion && this.hasFavoriteTarget) {
          this.updatePlayerActions()
        }
      }
      else if (!isPlayerInitialized) {
        this.setupProgressCircle()
        
        if (this.reactRoot) {
          this.reactRoot.unmount()
        }
        this.reactRoot = createRoot(this.historyTarget)
        this.reactRoot.render(
          <PlayHistoryContainer
            closeHistoryCallback={this.closeHistoryHandler.bind(this)}
            user_role={this.userValue.user_role}
          />
        )

        this.element.setAttribute('data-audio-player-initialized', 'true')
      }
      if (this.hasVolumeButtonTarget && this.hasVolumePanelTarget) {
        this.boundHandleDocClickForVolume = this.handleDocClickForVolume.bind(this)
        document.addEventListener("click", this.boundHandleDocClickForVolume, true)

        this.boundHandleKeydownForVolume = this.handleKeydownForVolume.bind(this)
        document.addEventListener("keydown", this.boundHandleKeydownForVolume)
      }

    } else {
      return
    }
  }

  disconnect() {
    // Remove document event listeners using the bound methods
    document.removeEventListener("play-song", this.boundHandleNewSongClick)
    document.removeEventListener("start-playlist", this.boundLoadSong)
    document.removeEventListener("load-playlist", this.boundLoadPlaylist)
    window.removeEventListener("favorite-toggled", this.boundHandleFavoriteToggled)

    cancelAnimationFrame(this.animationFrameId)
    if (this.hasPlayerTarget) {
      this.playerTarget.removeEventListener("playing", this.playingHandler)
      this.playerTarget.removeEventListener("pause", this.pauseHandler)
      this.playerTarget.removeEventListener("ended", this.endedHandler)
      this.playheadTarget.removeEventListener("click", this.seekHandler)
    }

    if (this.hasDownloadMenuTarget)
      this.downloadTarget.removeEventListener("click", this.downloadMenuOpenHandler)

    this.expandMenuButtonTarget.removeEventListener("click", this.actionsMenuOpenHandler)

    // Don't remove the initialized flag for turbo-permanent elements
    // This prevents re-initialization when the controller reconnects
    // However, if the element is being removed from DOM entirely, clean up
    if (!this.element.hasAttribute('data-turbo-permanent')) {
      this.cleanup()
    }

    if (this.boundToggleVolumePanel && this.hasVolumeButtonTarget) {
      this.volumeButtonTarget.removeEventListener("click", this.boundToggleVolumePanel)
    }
    if (this.boundHandleDocClickForVolume) {
      document.removeEventListener("click", this.boundHandleDocClickForVolume, true)
    }
    if (this.boundHandleKeydownForVolume) {
      document.removeEventListener("keydown", this.boundHandleKeydownForVolume)
    }
  }

  openLogin = openLogin

  handlePlaying(event) {
    this.isPlaying = true
    this.updatePlayPauseButton()
    this.drawPlayHead()
    this.updateProgressCircle(this.player.currentTime/this.player.duration)
    this.updateElapsedTime()
  }

  handlePause(event) {
    this.isPlaying = false
    cancelAnimationFrame(this.animationFrameId)
    this.updatePlayPauseButton()
  }

  handleEnded(event) {
    this.isPlaying = false
    cancelAnimationFrame(this.animationFrameId)
    this.updatePlayPauseButton()

    // Playlist handling logic. This should be ignored if the index is still -1
    if (this.currentPlaylistIndexValue >= 0) {
      this.currentPlaylistIndexValue++
      if (this.currentPlaylistIndexValue < this.playlistValue.length) {
        this.loadSong({ detail: this.playHistory[this.currentIndexValue] })
      }
    }
  }

  handleSeek(event) {
    if (this.hasPlayerSrc()) {
      this.seek(event)
    }
  }

  handleVolume(event) {
    const slider = event.target
    const amount = slider.value / 100
    this.player.volume = amount

    const percent = (slider.value / slider.max) * 100
    slider.style.setProperty('--val', `${percent}%`)
  }

  toggleVolumePanel = (e) => {
    e.stopPropagation()
    this.hoverConnectOff()
    if (this.volumePanelTarget.hasAttribute("hidden")) {
      this.openVolumePanel()
    } else {
      this.closeVolumePanel()
    }
  }

  openVolumePanel() {
    const el = this.volumePanelTarget
    el.hidden = false
    this.volumeButtonTarget.classList.add("tooltip-disabled")
    const input = el.querySelector("input")
    if (input) input.focus()
  }

  closeVolumePanel() {
    const el = this.volumePanelTarget
    el.hidden = true
    this.volumeButtonTarget.classList.remove("tooltip-disabled")
  }

  handleDocClickForVolume = (e) => {
    const volumeItem = this.volumeButtonTarget?.closest(".action.volume")
    if (!volumeItem) return
    if (!volumeItem.contains(e.target)) this.closeVolumePanel()
  }

  handleKeydownForVolume = (e) => {
    if (e.key === "Escape") this.closeVolumePanel()
  }

  // Canvases for drawing waveform and playhead
  setupCanvases() {
    this.waveformCanvas = this.waveformTarget
    this.playheadCanvas = this.playheadTarget

    // Ensure both canvases have the same dimensions
    const width = "479"
    const height = "52"

    // Only set dimensions if they haven't been set or are different
    // Setting canvas dimensions clears the content, so we avoid it if unnecessary
    if (this.waveformCanvas.width !== parseInt(width) || this.waveformCanvas.height !== parseInt(height)) {
      this.waveformCanvas.width = width
      this.waveformCanvas.height = height
    }

    if (this.playheadCanvas.width !== parseInt(width) || this.playheadCanvas.height !== parseInt(height)) {
      this.playheadCanvas.width = width
      this.playheadCanvas.height = height
    }
  }

  setupProgressCircle(){
    // Destroy existing progress circle if it exists to avoid duplicates
    if (this.progressCircle) {
      this.progressCircle.destroy()
    }

    this.progressCircle = new ProgressBar.Circle(this.playPauseButtonTarget, {
      strokeWidth: 10,
      easing: 'easeInOut',
      duration: 1400,
      color: 'var(--purple)',
      trailColor: 'var(--light-purple)',
      trailWidth: 10,
      svgStyle: null
    });
  }

  // Play / Pause logic
  togglePlayPause() {
    if (!this.hasPlayerSrc()) return
    if (this.isPlaying) {
      this.pause()
    } else {
      this.play()
    }
  }

  play() {
    this.player.play()
  }
  pause() {
    this.player.pause()
  }

  updatePlayPauseButton() {
    const play = this.playTarget
    const pause = this.pauseTarget

    if (this.hasPlayerSrc()) {
      play.classList.remove("disabled")
    } else {
      play.classList.add("disabled")
    }
    if (this.isPlaying) {
      // show pause icon
      pause.classList.remove("hide")
      play.classList.add("hide")
    } else {
      // show play icon
      play.classList.remove("hide")
      pause.classList.add("hide")
    }
  }

  loadPlaylist(event) {
    this.currentIndexValue = -1 // reset the user history position to default starting position
    this.playlistValue = event.detail.items // save songVersions array to playlist
    this.currentPlaylistIndexValue = event.detail.currentIndex || 0 // set playlist index to song index or first song
  }

  handleNewSongClick(event) {
    // Check if audio player is ready to handle the event
    if (!this.isReady()) {
      return
    }

    // When a new song is clicked from a browse list, set the index back to -1 to reset the play history position
    this.currentIndexValue = -1
    this.loadSong(event)
  }

  isReady() {
    return !!(this.hasPlayerTarget && this.boundHandleNewSongClick && !this.isLoading)
  }

  async loadSong(event) {
    // Clear out any existing canvas waveform
    this.clearCanvas()
    this.progressCircle.animate(0)
    // Clear any previous waveform data so the playhead doesn't draw the old waveform while the new one is loading
    this.waveformData = []

    if (this.isLoading) return

    this.currentSongVersion = event.detail

    this.currentSongVersionIdValue = this.currentSongVersion.id
    this.currentSongId = this.currentSongVersion.song_id

    if (this.currentRequest) {
      this.currentRequest.abort()
    }
    this.isLoading = true
    this.fetchWaveformData(this.currentSongVersion.id)
    try {
      const svUrl = await this.fetchNewSongVersionUrl(this.currentSongVersion.id)

      // Return if the fetched song's url is already set on the audio player
      if (this.player.src == svUrl) return
      this.updateSongInfo(
        this.currentSongVersion.id,
        this.currentSongVersion.display_name,
        this.currentSongVersion.artist,
        this.currentSongVersion.player_art
      )
      // Only add to history if the song being loaded is not from the history queue (index 0-20)
      if (parseInt(this.currentIndexValue) == -1) {
        this.addToHistory(event)
      }
      this.player.src = svUrl
      this.player.play()
      this.updatePlayerActions()
      this.playerAsideTarget.classList.add("has-song-version")
    } catch (error) {
      console.error("Error fetching song URL:", error)
    } finally {
      this.isLoading = false
      this.currentRequest = null
    }
  }

  async fetchNewSongVersionUrl(svId) {
    // Get a freshly signed url because the Algolia data is stale.
    const response = await fetch(`/api/v1/song_versions/${svId}/get_file_url`)
    if (!response.ok) {
      throw new Error("Network response was not ok")
    }
    const data = await response.json()
    return data.file_url
  }

  clearCanvas() {
    this.waveformCanvas
      .getContext("2d")
      .clearRect(0, 0, this.waveformCanvas.width, this.waveformCanvas.height)
    this.playheadCanvas
      .getContext("2d")
      .clearRect(0, 0, this.playheadCanvas.width, this.playheadCanvas.height)
  }

  updateSongInfo(svId, displayName, artist, playerArt) {
    this.thumbnailTarget.src = playerArt
    this.thumbnailTarget.style.visibility = "visible"
    // toggle the playable class on song-info element
    //this.infoTarget.style.backgroundColor = "#eee"
    // set the title and artist info in the song-info element
    this.titleTarget.textContent = displayName 
    this.titleTarget.href = `/browse/${svId}-${this.sanitizedTitle(displayName)}`
    this.artistTarget.textContent = " by " + artist.name
    this.artistTarget.href = `/artists/${artist.slug}`

    if (displayName.length > 15 || artist.name.length > 15) {
      this.infoTarget.classList.add("truncated")
    } else {
      this.infoTarget.classList.remove("truncated")
    }
  }

  updatePlayerActions() {
    const favoriteIcon = this.favoriteTarget.querySelector(".favorite-icon")
    const favorited = this.favoriteSongVersions

    /* set up correct icon display behavior for favorite button */
    this.actionsTarget.classList.remove("hide")
    if (favorited.includes(this.currentSongVersion.id)) {
      favoriteIcon.classList.add("selected")
    } else {
      favoriteIcon.classList.remove("selected")
    }

    /* Set up download options menu to have correct links*/
    if (this.hasDownloadMenuTarget) {
      const downloadMenu = this.downloadMenuTarget
      downloadMenu.querySelector(
        ".mm-low-res"
      ).children[0].href = `/song_versions/${this.currentSongVersionIdValue}/download/low`
      downloadMenu.querySelector(
        ".mm-high-res"
      ).children[0].href = `/song_versions/${this.currentSongVersionIdValue}/download/high`

      const stemsOption = downloadMenu.querySelector(".mm-download-stems")
      if (this.currentSongVersion.has_stems) {
        stemsOption.children[0].href = `/song_versions/${this.currentSongVersionIdValue}/download_stems`
        stemsOption.classList.remove("disabled")
      } else {
        stemsOption.children[0].href = `#`
        stemsOption.classList.add("disabled")
      }
    }

    /* Set up add to project search button */
    if (this.hasProjectSearchTarget) {
      this.projectSearchTarget.removeEventListener("click", this.addToProjectSearchHandler)
      this.projectSearchTarget.addEventListener("click", this.addToProjectSearchHandler)
    }

    /* Set up buy license button */
    this.licenseTarget.removeEventListener("click", this.licenseHandler)
    this.licenseTarget.addEventListener("click", this.licenseHandler)
    
    if(this.currentSongVersion.has_click_licenses) {
      [...this.licenseTarget.children].forEach(child => child.textContent = "License")
      this.licenseTarget.setAttribute("data-connect-text", "License")
    } else {
      [...this.licenseTarget.children].forEach(child => child.textContent = "Custom License")
      this.licenseTarget.setAttribute("data-connect-text", "Custom License")
    }

    /* Set up hover connect behavior */
    const actions = [...this.actionsTarget.children].filter(
      (child) => child.classList.contains("action") && !child.classList.contains("tooltip")
    )

    /* setup expanded menu action behavior */
    const expandedActions = [...this.expandedMenuTarget.children]
    expandedActions.forEach((action) => {
      let triggeredAction = action.getAttribute("data-action")
      let func =
        triggeredAction === "lyrics" && this.currentSongVersion.lyrics
          ? this.displayLyricsHandler
          : triggeredAction === "history"
          ? this.openHistoryHandler
          : triggeredAction === "mixtape"
          ? this.addToMixtapeHandler
          : triggeredAction === "login"
          ? this.triggerLoginHandler
          : null //default
      if (func) {
        action.classList.remove("disabled")
        action.removeEventListener("click", func)
        action.addEventListener("click", func)
      } else {
        // Hacky way to address this.
        action.removeEventListener("click", this.displayLyricsHandler)
        action.classList.add("disabled")
      }
    })
  }

  handleDisplayLyrics = () => {
    const modal = document.querySelector("[data-controller='modal']")
    const frame = document.querySelector("turbo-frame#active-modal-content")

    if (modal && frame) {
      document.dispatchEvent(new CustomEvent("modal:open"))
      frame.innerHTML = ""
      frame.setAttribute("src", `/song_versions/lyrics/${this.currentSongVersionIdValue}`)
    }
  }

  handleOpenHistory(e) {
    this.historyTarget.classList.add("expanded")
    this.playerAsideTarget.classList.add("expanded")
    /** Set up playerHistory Fetch event */

    const triggerPlayerFetchEvent = new CustomEvent("playerHistory", {
      detail: {
        show: this.historyTarget.classList.contains("expanded"),
      },
    })
    window.dispatchEvent(triggerPlayerFetchEvent)
  }

  closeHistoryHandler(event) {
    this.historyTarget.classList.remove("expanded")
    this.playerAsideTarget.classList.remove("expanded")
  }

  openDownloadMenu(event) {
    this.downloadMenuTarget.classList.add("expanded")
    document.body.addEventListener("click", this.clickOffDownloadMenu.bind(this))
  }

  openActionsMenu(event) {
    event.stopPropagation()
    this.expandedMenuTarget.classList.add("expanded")
    document.body.addEventListener("click", this.clickOffActionsMenu.bind(this))
  }

  clickOffDownloadMenu(e) {
    if (!e.target.closest(["div.download-options-menu", ".download"])) {
      document.body.removeEventListener("click", this.clickOffDownloadMenu.bind(this))
      this.downloadMenuTarget.classList.remove("expanded")
    }
  }

  clickOffActionsMenu(e) {
    e.stopPropagation()
    if (
      !e.target.closest([
        "div.player-actions-menu",
        ".expand-player-actions-menu .horizontal-ellipsis",
      ])
    ) {
      document.body.removeEventListener("click", this.clickOffActionsMenu.bind(this))
      this.expandedMenuTarget.classList.remove("expanded")
    }
  }

  hoverConnectOn(e) {
    const anchorEl = e.currentTarget
    const connectText = anchorEl.getAttribute("data-connect-text")
    let hoverContent = this.currentSongVersion.exclusive ? `<span style="margin: 10px 0px; text-align: center;">This song has some restrictions, <br> contact us for conditions and terms.</span>`  
    : this.currentSongVersion.vintage ? `<span style="margin: 10px 10px; text-align: center;">Vintage songs are priced at a higher rate, <br> contact us for a custom license.</span>` 
    : connectText
    
    if(connectText === "Add To Favorites")
      hoverContent = connectText
    // Don't show hover connect if the volume panel is open
    if (!this.volumePanelTarget.hasAttribute("hidden")) {
      return;
    }

    let iDiv = document.createElement("div")
    iDiv.className = "connect-label connected"
    iDiv.innerHTML = hoverContent
    iDiv.style.padding = (hoverContent !== connectText) ? "20px" : "10px"
    this.hoverConnectTarget.append(iDiv)

    const r = anchorEl.getBoundingClientRect();
    const labelW = iDiv.offsetWidth
    const labelH = iDiv.offsetHeight
    const offsetLeft = (r.left < window.innerWidth / 2) ? -10 : -10

    const leftPositionDestination = r.left + (r.width / 2) - (labelW / 2) + offsetLeft

    iDiv.style.left = `${Math.min(leftPositionDestination, window.innerWidth - labelW)}px`
    iDiv.style.top = `${r.top - labelH - 6}px`
  }

  hoverConnectOff(e) {
    while (this.hoverConnectTarget.firstChild) {
      this.hoverConnectTarget.removeChild(this.hoverConnectTarget.firstChild)
    }
  }

  toggleFavorite = async (e) => {
    let isFavorited = this.favoriteSongVersions.includes(this.currentSongVersion.id)
    const favoriteIcon = this.favoriteTarget.querySelector(".favorite-icon")
    const request = isFavorited ? "remove" : "add"
    const resp = favoriteUnfavoriteSong(this.currentSongVersion.id, request)

    request === "add"
      ? favoriteIcon.classList.add("selected")
      : favoriteIcon.classList.remove("selected")

    const result = await resp.then((data) => data.json())

    if (!result.success) {
      request === "remove"
        ? favoriteIcon.classList.add("selected")
        : favoriteIcon.classList.remove("selected")
      return
    }

    request === "add"
      ? this.favoriteSongVersions.push(this.currentSongVersion.id)
      : (this.favoriteSongVersions = this.favoriteSongVersions.filter(
          (svId) => svId !== this.currentSongVersion.id
        ))
  }

  handleFavoriteToggled = (event) => {
    const { songVersionId, request } = event.detail

    // Update the local favorites list
    if (request === "add" && !this.favoriteSongVersions.includes(songVersionId)) {
      this.favoriteSongVersions.push(songVersionId)
    } else if (request === "remove") {
      this.favoriteSongVersions = this.favoriteSongVersions.filter(
        (svId) => svId !== songVersionId
      )
    }

    // Update the favorite icon if this is the currently playing song
    if (this.currentSongVersion && this.currentSongVersion.id === songVersionId && this.hasFavoriteTarget) {
      const favoriteIcon = this.favoriteTarget.querySelector(".favorite-icon")
      if (favoriteIcon) {
        if (request === "add") {
          favoriteIcon.classList.add("selected")
        } else {
          favoriteIcon.classList.remove("selected")
        }
      }
    }
  }

  handleLicense(e) {
     const modal = document.querySelector("[data-controller='modal']");
    if (modal) {
      const modalController = this.application.getControllerForElementAndIdentifier(modal, "modal")
      const openEvent = new CustomEvent("modal:open", { detail: { size: "large" } });
      modalController?.open(openEvent)
    }
    if(this.currentSongVersion.has_click_licenses){
      Turbo.visit(`/song_versions/${this.currentSongVersion.id}/licenses`, {
        frame: "active-modal-content",
      })
    } else {
      handleCustomLicenseRequest(this.currentSongVersion)
    }
  }

  handleProjectSearch(e) {
    const modal = document.querySelector("[data-controller='modal']");
    if (modal) {
      const modalController = this.application.getControllerForElementAndIdentifier(modal, "modal");
      const openEvent = new CustomEvent("modal:open", { detail: { size: "small" } });
      modalController?.open(openEvent);
    }
    Turbo.visit(`/searchbench/${this.currentSongVersion.id}`, {
      frame: "active-modal-content",
    })
  }

  handleAddToMixtape(e){
    const modal = document.querySelector("[data-controller='modal']");
    if (modal) {
      const modalController = this.application.getControllerForElementAndIdentifier(modal, "modal");
      const openEvent = new CustomEvent("modal:open", { detail: { size: "small" } });
      modalController?.open(openEvent);
    }
    Turbo.visit(`/mixbench/${this.currentSongVersion.id}`, {
      frame: "active-modal-content",
    })
  }

  async fetchWaveformData(svId) {
    try {
      const response = await fetch(`/api/v1/song_versions/${svId}/get_waveform_data`)
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      const json = await response.json()
      this.waveformData = json["waveform_data"]

      this.drawWaveform(this.waveformData)
    } catch (error) {
      console.error("Error fetching waveform data:", error)
    }
  }

  drawWaveform(data) {
    const canvas = this.waveformTarget
    const ctx = canvas.getContext("2d")
    const width = canvas.width
    const height = canvas.height * 2

    ctx.fillStyle = "#E9DDFF"

    data.forEach((amplitude, index) => {
      const x = (index / data.length) * width
      const barHeight = (amplitude / 100) * height - 5
      ctx.fillRect(x, (height - barHeight) / 2, 2, barHeight)
    })
  }

  drawPlayHead() {
    const ctx = this.playheadCanvas.getContext("2d")
    const width = this.playheadCanvas.width
    const height = this.playheadCanvas.height * 2

    const player = this.playerTarget
    const currentTime = player.currentTime
    const duration = player.duration
    const progress = currentTime / duration

    // Clear the entire playhead canvas
    ctx.clearRect(0, 0, width, height)

    // Draw the playhead
    ctx.fillStyle = "#634B9F"
    if (this.waveformData) {
      this.waveformData.forEach((amplitude, index) => {
        const x = (index / this.waveformData.length) * width
        if (x <= progress * width) {
          const barHeight = (amplitude / 100) * height - 5
          ctx.fillRect(x, (height - barHeight) / 2, 2, barHeight)
        }
      })
    }

    if (this.isPlaying) {
      this.animationFrameId = requestAnimationFrame(() => {this.drawPlayHead()})
      if (currentTime % 2) {
        this.updateElapsedTime(currentTime, duration)
        this.updateProgressCircle(currentTime, duration)
      }
    }
  }

  updateElapsedTime(currentTime, duration) {
    this.elapsedTarget.textContent = this.formatTime(Math.round(currentTime))
    this.lengthTarget.textContent = " / " + this.formatTime(Math.round(duration))
  }

  updateProgressCircle(currentTime, duration){
    this.progressCircle.set(currentTime/duration)
  }

  formatTime(seconds) {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`
  }

  seek(event) {
    const rect = event.target.getBoundingClientRect()
    const offX = event.clientX - rect.left
    const seekPosition = (offX / rect.width) * this.player.duration
    this.player.currentTime = Math.round(seekPosition * 100) / 100
    this.progressCircle.animate(this.player.currentTime/this.player.duration);

    // If the song ended or paused, this will get it playing again.
    if (!this.isPlaying) {
      this.play()
    }
  }

  // User play history/queue functions
  async fetchPlayHistory() {
    const response = await fetch("/api/v1/users/get_play_history")
    const json = await response.json()
    const songs = json["data"] || []
    this.playHistory = songs.map((song) => {
      return {
        detail: JSON.parse(song.detail.song_versions[0].play_serializer),
      }
    })
    this.updateNavigationButtons()
  }

  async savePlayToServer(svId) {
    await fetch("/api/v1/users/add_play", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrf,
      },
      body: JSON.stringify({ song_version_id: svId }),
    })
  }

  addToHistory(event) {
    // Add the song version id to a list of the 20 most recent plays
    // We can make a server side request to add each song play to the user_actions table as a SongVersionListen

    // add the new song version to the top of the history queue
    this.playHistory.unshift(event)
    if (this.playHistory.length > 20) {
      // drop the last item from the history queue
      this.playHistory.pop(this.playHistory.length)
    }
    this.updateNavigationButtons()
    this.savePlayToServer(this.currentSongVersionIdValue)
  }

  forward() {
    // If there's a playlist loaded, use that for the next song version
    // First song is index 0, last song is at playlist.length - 1
    if (this.currentPlaylistIndexValue < this.playlistValue.length - 1) {
      this.currentPlaylistIndexValue++
      this.loadSong({ detail: this.playlistValue[this.currentPlaylistIndexValue] })
    }
    // Otherwise, use the user's play history
    // Most recent play is index 0, longest ago is index 19. Higher index == older play
    else if (this.currentIndexValue > 0) {
      this.currentIndexValue--
      this.loadSong(this.playHistory[this.currentIndexValue])
    }
    this.updateNavigationButtons()
  }

  back() {
    // If there's a playlist loaded, use that for the previous song version
    if (this.currentPlaylistIndexValue > 0) {
      this.currentPlaylistIndexValue--
      this.loadSong({ detail: this.playlistValue[this.currentPlaylistIndexValue] })
    }
    // Otherwise, use the user's play history
    else if (this.currentIndexValue < this.playHistory.length - 1) {
      this.currentIndexValue++
      this.loadSong(this.playHistory[this.currentIndexValue])
    }
    this.updateNavigationButtons()
  }

  updateNavigationButtons() {
    if (this.playlistValue.length > 0) {
      this.backButtonTarget.disabled = this.currentPlaylistIndexValue <= 0
      this.forwardButtonTarget.disabled =
        this.currentPlaylistIndexValue >= this.playHistory.length - 1
    } else {
      this.backButtonTarget.disabled = this.currentIndexValue >= this.playHistory.length - 1
      this.forwardButtonTarget.disabled = this.currentIndexValue <= 0
    }
  }

  hasPlayerSrc() {
    return this.player.src.length > 0
  }

  // State preservation methods for handling controller reconnection
  preserveState() {
    if (!this.hasPlayerTarget) return { isPlaying: false }

    return {
      isPlaying: !this.player.paused
    }
  }

  // Method to fully cleanup the audio player when truly destroying
  // This should only be called when the element is actually removed from DOM
  cleanup() {
    if (this.element.hasAttribute('data-audio-player-initialized')) {
      // Unmount React components
      if (this.reactRoot) {
        this.reactRoot.unmount()
      }

      // Clean up progress circle
      if (this.progressCircle) {
        this.progressCircle.destroy()
      }

      this.element.removeAttribute('data-audio-player-initialized')
    }
  }

  sanitizedTitle(title) {
    return title
      .toLowerCase()
      .replace(/\s/g, "-")
      .replace(/[^a-z0-9-]/g, "")
  }
}
